// --- React and Hooks Imports ---
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// --- Library Imports ---
import axios, { AxiosError, AxiosResponse } from 'axios';
import { motion } from 'framer-motion';
import {
    X, Send, RefreshCcw, Video, Image, FileText, Clock, Palette, Loader2, Check, Flag,
    AlertTriangle, MessageSquareWarning, ShieldBan, Ban, Smile, Plus, BarChart3
} from 'lucide-react';

// --- Local Imports ---
import { ReactComponent as EchoomiLogo } from './assets/echoomi-logo-trans.svg'; // Restored original import
import {
    SHOUT_COUNT, ENABLE_CONTENT_TYPE_TEXT, ENABLE_CONTENT_TYPE_VIDEO,
    ENABLE_CONTENT_TYPE_IMAGE, ENABLE_CONTENT_TYPE_POLL,
    DEFAULT_PLACEHOLDER_IMAGE, TEXT_COLOR_MODE,
    HEADLINE_MAX_CHARS, MESSAGE_MAX_WORDS,
} from './config.js'; // Assuming config.js is in the same directory or adjust path
import ShoutModal from './ShoutModal.tsx'; // Assuming ShoutModal.tsx is in the same directory
import CreateShoutModal, { INewShout as CreateModalPayload } from './CreateShoutModal.tsx'; // Assuming CreateShoutModal.tsx is in the same directory

// detect Android browsers for IME compatibility
const IS_ANDROID = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

// --- Inject CSS ---
const style = document.createElement('style');
style.textContent = `
  /* Base card styling */
  .shout-card { transition: transform 0.2s ease, border-color 0.2s ease; border: 2px solid transparent; }
  .shout-card:hover { transform: translateY(-2px); border-color: rgba(255, 255, 255, 0.1); }
  /* Color picker option styling */
  .color-option { width: 24px; height: 24px; border-radius: 50%; cursor: pointer; transition: transform 0.2s ease, border-color 0.2s ease; border: 2px solid transparent; }
  .color-option:hover { transform: scale(1.2); }
  .color-option.active { border-color: white; transform: scale(1.1); }
  /* Shared popover styling */
  .picker-popover { position: absolute; background-color: #1f2937; border: 1px solid #374151; border-radius: 8px; padding: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); z-index: 1000; }
  /* Color picker specific layout */
  .color-picker { display: flex; gap: 8px; }
  /* Flag picker specific layout and styling */
  .flag-picker { display: flex; flex-direction: column; gap: 4px; max-width: 300px; }
  .flag-picker button { background-color: #374151; color: #d1d5db; padding: 6px 10px; border-radius: 6px; text-align: left; font-size: 0.875rem; line-height: 1.25rem; transition: background-color 0.15s ease; border: none; cursor: pointer; width: 100%; }
  .flag-picker button:hover { background-color: #4b5563; }
  .flag-picker p { font-size: 0.75rem; line-height: 1rem; color: #9ca3af; margin-top: 8px; border-top: 1px solid #374151; padding-top: 8px; }
  /* CSS Fix for bold text color issue */
  .shout-content-display strong { color: inherit !important; }
  .flag-button-disabled { color: #6b7280; cursor: default; display: flex; align-items: center; }

  .custom-scrollbar-reactions::-webkit-scrollbar { width: 4px; }
  .custom-scrollbar-reactions::-webkit-scrollbar-track { background: rgba(0,0,0,0.1); border-radius: 2px; }
  .custom-scrollbar-reactions::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
  .custom-scrollbar-reactions::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
`;
if (!document.getElementById('echoomi-styles')) {
    style.id = 'echoomi-styles';
    document.head.appendChild(style);
}


// --- YouTube URL Handling Functions ---
const extractYouTubeVideoId = (url: string | null): string | null => {
    if (!url) return null;
    const patterns = [
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
        /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?]+)/,
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?]+)/,
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([^?]+)/,
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*&v=([^&]+)/
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) return match[1];
    }
    return null;
};

const convertToEmbedUrl = (url: string | null): string => {
    if (!url) return '';
    if (url.includes('/embed/')) return url; // Already an embed URL
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) return url; // Return original if ID extraction fails

    let params = '';
    try {
        const urlObj = new URL(url);
        if (urlObj.searchParams.has('t')) {
            let timeParam = urlObj.searchParams.get('t')!;
            const timeMatch = timeParam.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?/);
            if (timeMatch) {
                const hours = parseInt(timeMatch[1] || '0');
                const minutes = parseInt(timeMatch[2] || '0');
                const seconds = parseInt(timeMatch[3] || '0');
                const totalSeconds = hours * 3600 + minutes * 60 + seconds;
                if (totalSeconds > 0) params += `?start=${totalSeconds}`;
            }
        }
        // Add other allowed parameters if present
        const allowedParams = ['start', 'autoplay', 'mute', 'loop']; // 'controls' is often default or handled by player
        urlObj.searchParams.forEach((value, key) => {
            if (allowedParams.includes(key) && key !== 't') { // Avoid double 'start'
                if (!params.includes('start=') || key !== 'start') {
                    params += params ? '&' : '?';
                    params += `${key}=${value}`;
                }
            }
        });
    } catch (e) {
        console.warn('Failed to parse URL parameters for YouTube link:', e, "URL:", url);
    }
    // Using googleusercontent.com for enhanced privacy if that's the intent, otherwise youtube.com/embed
    return `https://www.youtube.com/embed/${videoId}${params}`;
    // If googleusercontent.com is strictly needed:
    // return `https://www.youtube.com/embed/$${videoId}${params}`;
};


// --- Type Definitions ---
export interface IFormatting {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    alignment: 'left' | 'center' | 'right';
    textColor: string;
}

export interface IPollOption {
    id: string;
    text: string;
    emoji: string;
    votes: number;
}

export interface IShout {
    id: string;
    headline: string;
    content_type: 'text' | 'video' | 'image' | 'poll';
    text_content: string | null;
    video_link: string | null;
    image_url: string | null;
    duration: number;
    reach: number;
    echoes: number;
    formatting: IFormatting;
    created_at: string;
    secondsRemaining: number;
    reactions: number;
    reactions_json: Record<string, number>;
    poll_options: IPollOption[] | null;
    total_votes: number | null;
    poll_results: any; // Raw JSONB from backend, transformed into poll_options
    poll_responses: number; // Raw count from backend, transformed into total_votes
    user_voted_option_id?: string | null;
}

export interface INewShoutDataForModal {
    headline: string;
    contentType: 'text' | 'video' | 'image' | 'poll';
    textContent: string;
    videoLink: string;
    imageUrl: string;
    formatting: IFormatting;
    pollOptionsData: { text: string, emoji: string }[];
}

type ShoutSlotStatus = 'visible' | 'exiting' | 'entering' | 'empty' | 'loading';
interface IShoutSlot {
    slotId: string;
    data: IShout | null;
    status: ShoutSlotStatus;
}
type AutomodFailureReason = 'banned_phrase' | 'low_quality' | 'link_detected' | null;
interface AutomodRejectionResponse {
    status: 'rejected';
    reason: AutomodFailureReason;
    message: string;
}

// --- Constants ---
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const EXIT_ANIMATION_DURATION_MS = 800;
const ENTRY_ANIMATION_DURATION_MS = 300;

const FLAG_REASONS = [
    { key: 'CSAM', label: 'Child sexual abuse material (CSAM)' },
    { key: 'Terrorism', label: 'Terrorism or terrorist recruitment' },
    { key: 'Threats', label: 'Credible threats of violence' },
    { key: 'NCII', label: 'Non-consensual intimate imagery' },
    { key: 'CourtOrder', label: 'Court-ordered removal request' },
    { key: 'Copyright', label: 'Copyright infringement' },
    { key: 'Defamation', label: 'Defamation targeting real people' },
    { key: 'Medical', label: 'False or dangerous medical advice' },
    { key: 'IllegalGoods', label: 'Promotion of illegal goods/services' }
];
const colorOptions = [
    { name: 'purple', primary: 'bg-purple-600', secondary: 'text-purple-400', hover: 'hover:bg-purple-700', color: '#9333ea' },
    { name: 'blue', primary: 'bg-blue-600', secondary: 'text-blue-400', hover: 'hover:bg-blue-700', color: '#2563eb' },
    { name: 'green', primary: 'bg-green-600', secondary: 'text-green-400', hover: 'hover:bg-green-700', color: '#16a34a' },
    { name: 'red', primary: 'bg-red-600', secondary: 'text-red-400', hover: 'hover:bg-red-700', color: '#dc2626' },
    { name: 'amber', primary: 'bg-amber-600', secondary: 'text-amber-400', hover: 'hover:bg-amber-700', color: '#d97706' },
    { name: 'pink', primary: 'bg-pink-600', secondary: 'text-pink-400', hover: 'hover:bg-pink-700', color: '#db2777' },
    { name: 'teal', primary: 'bg-teal-600', secondary: 'text-teal-400', hover: 'hover:bg-teal-700', color: '#0d9488' },
    { name: 'indigo', primary: 'bg-indigo-600', secondary: 'text-indigo-400', hover: 'hover:bg-indigo-700', color: '#4f46e5' }
];
const defaultColorOption = { name: 'default', primary: 'bg-gray-600', secondary: 'text-gray-400', hover: 'hover:bg-gray-700', color: '#4B5563' };

// --- Animation Variants (Framer Motion) ---
const cardCellVariants = {
    initial: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeOut' } },
    entering: { opacity: 1, scale: 1, transition: { duration: ENTRY_ANIMATION_DURATION_MS / 1000, ease: 'easeOut' } },
    exiting: {
        opacity: [1, 0.75, 1, 0.5, 0],
        scale: [1, 1, 1, 0.98, 0.95],
        transition: {
            duration: EXIT_ANIMATION_DURATION_MS / 1000,
            times: [0, 0.2, 0.4, 0.7, 1],
            ease: 'easeInOut'
        }
    },
    empty: { opacity: 0, scale: 0.9, transition: { duration: 0.3 } },
    loading: { opacity: 0.5, scale: 0.98 }
};

// --- ShoutCard Component ---
interface ShoutCardProps {
    slot: IShoutSlot;
    onExpire: (slotId: string, expiredShoutId: string) => void;
    onClick: (shoutData: IShout) => void;
    getThemeColors: () => typeof colorOptions[0];
}

const ShoutCard: React.FC<ShoutCardProps> = ({ slot, onExpire, onClick, getThemeColors }) => {
    const { slotId, data, status } = slot;
    const startTimeRef = useRef<number>(Date.now());
    const [remaining, setRemaining] = useState<number>(data ? data.duration : 0);
    const [expiredTriggered, setExpiredTriggered] = useState<boolean>(false);

    useEffect(() => {
        if (!data || !(status === 'visible' || status === 'entering')) {
            setRemaining(0); return;
        }
        startTimeRef.current = Date.now();
        setExpiredTriggered(false);
        const exitDurationSec = EXIT_ANIMATION_DURATION_MS / 1000;

        const interval = setInterval(() => {
            const elapsed = (Date.now() - startTimeRef.current) / 1000;
            const newRemaining = Math.max(data.duration - elapsed, 0);
            setRemaining(newRemaining);
            if (newRemaining <= exitDurationSec && !expiredTriggered) {
                setExpiredTriggered(true);
                onExpire(slotId, data.id);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [data, status, slotId, onExpire, expiredTriggered]);

    const theme = getThemeColors();
    const healthWidth = data ? `${(remaining / data.duration) * 100}%` : '0%';
    const exitDurationSec = EXIT_ANIMATION_DURATION_MS / 1000;

    const cardReactions = data?.reactions_json ? Object.entries(data.reactions_json)
        .filter(([, count]) => count > 0)
        .sort(([, countA], [, countB]) => countB - countA)
        .slice(0, 3)
        : [];
    const totalCardReactions = data?.reactions || 0;

    return (
        <motion.div
            key={slotId}
            variants={cardCellVariants}
            initial="initial"
            animate={status}
            className={`bg-gray-800 rounded-lg p-4 relative overflow-hidden shout-card min-h-[145px] flex flex-col justify-between ${!data && status !== 'exiting' ? 'items-center justify-center' : ''}`}
            onClick={() => data && (status === 'visible' || status === 'entering') && onClick(data)}
            style={{ cursor: data && (status === 'visible' || status === 'entering') ? 'pointer' : 'default' }}
        >
            {data && (status === 'visible' || status === 'entering' || status === 'exiting') && (
                <>
                    {data.duration > 0 && (
                        <motion.div
                            key={`${data.id}-hb`}
                            className={`absolute bottom-0 left-0 h-1 ${theme.primary}`}
                            animate={status === 'exiting' ? { width: '0%', transition: { duration: exitDurationSec, ease: 'linear' } } : { width: healthWidth, transition: { duration: 0.1, ease: 'linear' } }}
                        />
                    )}
                    <div className="absolute top-4 right-4 bg-gray-700 p-2 rounded-full shadow">
                        {data.content_type === 'text' ? (<span className="text-green-400" title="Text"><FileText size={20} /></span>)
                            : data.content_type === 'video' ? (<span className="text-red-400" title="Video"><Video size={20} /></span>)
                            : data.content_type === 'image' ? (<span className="text-blue-400" title="Image"><Image size={20} /></span>)
                            : data.content_type === 'poll' ? (<span className="text-purple-400" title="Poll"><BarChart3 size={20} /></span>)
                            : (<span className="text-gray-400" title="Unknown"><FileText size={20} /></span>)
                        }
                    </div>
                    <h2 className="text-lg font-semibold mb-2 mr-14 line-clamp-2 min-h-[3rem]">{data.headline}</h2>

                    {/* UPDATED STATS SECTION */}
                    <div className="flex justify-between items-center text-gray-400 text-sm mt-auto">
                        <div className="flex items-center gap-3"> {/* Container for left-aligned stats */}
                            {/* 1. Echoes */}
                            <div className="flex items-center gap-1" title="Echoes">
                                <RefreshCcw size={14} className="mr-0.5" /> <span>{data.echoes}</span>
                            </div>

                            {/* 2. Reaction Preview (always shown) */}
                            <div className="flex items-center gap-1" title={`Reactions: ${totalCardReactions}`}>
                                {cardReactions.length > 0 ? (
                                    <>
                                        <div className="flex -space-x-1 items-center">
                                            {cardReactions.map(([emoji]) => (
                                                <div
                                                    key={`${data.id}-card-reaction-preview-${emoji}`} // Unique key for reaction previews
                                                    className="w-4 h-4 rounded-full bg-gray-900/70 flex items-center justify-center text-xs border border-gray-800/50"
                                                >
                                                    {emoji}
                                                </div>
                                            ))}
                                        </div>
                                        <span className="text-xs ml-0.5">{totalCardReactions}</span>
                                    </>
                                ) : (
                                    <>
                                        <Smile size={14} />
                                        <span className="text-xs">{totalCardReactions}</span> {/* Shows '0' if no reactions */}
                                    </>
                                )}
                            </div>

                            {/* 3. Poll Response Count (only for polls) */}
                            {data.content_type === 'poll' && data.total_votes !== null && (
                                <div className="flex items-center gap-1" title={`Total Votes: ${data.total_votes}`}>
                                    <BarChart3 size={14} /> <span>{data.total_votes}</span>
                                </div>
                            )}
                        </div>
                        {/* Time Remaining (right-aligned) */}
                        <div className="flex items-center gap-1" title="Time Remaining">
                            <Clock size={14} className="text-yellow-400" /> <span>{Math.floor(remaining)}s</span>
                        </div>
                    </div>
                    {/* END OF UPDATED STATS SECTION */}
                </>
            )}
            {status === 'empty' && (<div className="text-center text-gray-600"> <X size={30} /> <p className="text-xs mt-1">Slot Empty</p> </div>)}
            {status === 'loading' && (<div className="text-center text-gray-500"> <Loader2 size={30} className="animate-spin" /> </div>)}
        </motion.div>
    );
};


// --- Main Parent Component (EchoVoid) ---
let currentSessionToken: string | null = null;

// --- Device ID Handling ---
let currentDeviceId = localStorage.getItem('echoomi_device_id');
if (!currentDeviceId) {
    currentDeviceId = `dev-${Date.now().toString(36)}-${Math.random().toString(36).substring(2)}`;
    localStorage.setItem('echoomi_device_id', currentDeviceId);
}
// console.log('Current Device ID:', currentDeviceId); // Optional: for debugging

export default function EchoVoid() {
    const [shoutSlots, setShoutSlots] = useState<IShoutSlot[]>([]);
    const [modalShout, setModalShout] = useState<IShout | null>(null);
    const [composing, setComposing] = useState<boolean>(false);

    const getDefaultContentType = (): 'text' | 'video' | 'image' | 'poll' => {
        if (ENABLE_CONTENT_TYPE_TEXT) return 'text';
        if (ENABLE_CONTENT_TYPE_POLL) return 'poll';
        if (ENABLE_CONTENT_TYPE_VIDEO) return 'video';
        if (ENABLE_CONTENT_TYPE_IMAGE) return 'image';
        return 'text'; // Fallback
    };
    
    const getInitialPollOptions = () => [
        { text: '', emoji: '' },
        { text: '', emoji: '' },
    ];

    const [initialModalShoutData, setInitialModalShoutData] = useState<INewShoutDataForModal>({
        headline: '',
        contentType: getDefaultContentType(),
        textContent: '',
        videoLink: '',
        imageUrl: DEFAULT_PLACEHOLDER_IMAGE,
        formatting: { bold: false, italic: false, underline: false, alignment: 'left', textColor: 'white' },
        pollOptionsData: getDefaultContentType() === 'poll' ? getInitialPollOptions() : [],
    });

    const [themeColor, setThemeColor] = useState<string>('purple');
    const [colorPickerOpen, setColorPickerOpen] = useState<boolean>(false);
    const [echoedShoutIds, setEchoedShoutIds] = useState<Set<string>>(new Set());
    const [flagPopoverOpen, setFlagPopoverOpen] = useState<boolean>(false);
    const [flaggedShoutIds, setFlaggedShoutIds] = useState<Set<string>>(new Set());
    const [automodErrorModalOpen, setAutomodErrorModalOpen] = useState<boolean>(false);
    const [automodFailureReason, setAutomodFailureReason] = useState<AutomodFailureReason>(null);
    const [currentUserReactionsForModal, setCurrentUserReactionsForModal] = useState<Set<string>>(new Set());
    const [userVotedPollOptions, setUserVotedPollOptions] = useState<Record<string, string>>({});


    const slotsRef = useRef<IShoutSlot[]>([]);
    useEffect(() => { slotsRef.current = shoutSlots; }, [shoutSlots]);
    const apiClient = axios.create({ baseURL: API_BASE_URL });

    // Response interceptor to capture the session token
    apiClient.interceptors.response.use(
        (response) => {
            const token = response.headers['x-session-token'];
            if (token) {
                currentSessionToken = token;
                // console.log('Session token captured:', currentSessionToken); // Optional: for debugging
            }
            return response;
        },
        (error) => {
            if (error.response && error.response.headers && error.response.headers['x-session-token']) {
                currentSessionToken = error.response.headers['x-session-token'];
                // console.log('Session token captured from error response:', currentSessionToken); // Optional: for debugging
            }
            return Promise.reject(error);
        }
    );

    // Request interceptor to send the session token and device ID
    apiClient.interceptors.request.use(
        (config) => {
            if (currentSessionToken) {
                config.headers['x-session-token'] = currentSessionToken;
                // console.log('Sending with session token:', currentSessionToken); // Optional: for debugging
            }
            if (currentDeviceId) {
                config.headers['x-device-id'] = currentDeviceId;
                // console.log('Sending with device ID:', currentDeviceId); // Optional: for debugging
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );
    const colorPickerButtonRef = useRef<HTMLButtonElement>(null);
    const colorPickerPopoverRef = useRef<HTMLDivElement>(null);
    const flagButtonRefForModal = useRef<HTMLButtonElement>(null); // Ref for the flag button inside the modal
    const flagPopoverRef = useRef<HTMLDivElement>(null);


    const getThemeColors = useCallback(() => {
        return colorOptions.find(c => c.name === themeColor) || colorOptions[0] || defaultColorOption;
    }, [themeColor]);


    async function fetchShouts(limit: number, excludeIds: string[] = []): Promise<IShout[]> {
        try {
            const params: any = { limit };
            const validExcludeIds = excludeIds.filter(id => id && id.length > 0);
            if (validExcludeIds.length > 0) params.exclude = validExcludeIds.join(',');

            const response = await apiClient.get<IShout[]>('/shouts/random', { params });
            return response.data.map(shout => {
                let parsedPollOptions: IPollOption[] | null = null;
                let parsedTotalVotes: number | null = null;

                if (shout.content_type === 'poll' && shout.poll_results) {
                    let pollResultsData = shout.poll_results;
                    if (typeof pollResultsData === 'string') {
                        try {
                            pollResultsData = JSON.parse(pollResultsData);
                        } catch (e) {
                            console.error("Error parsing poll_results JSON:", e, "Data:", pollResultsData);
                            pollResultsData = { options: [] }; // Fallback
                        }
                    }
                    parsedPollOptions = (pollResultsData?.options || []).map((opt: any) => ({
                        id: opt.id || String(Math.random()), // Ensure ID exists, fallback if necessary
                        text: opt.text || '',
                        emoji: opt.emoji || '', // Default emoji handled in display logic
                        votes: Number(opt.votes) || 0,
                    }));
                    parsedTotalVotes = Number(shout.poll_responses) || 0;
                }

                return {
                    ...shout,
                    secondsRemaining: shout.duration,
                    reactions: Number(shout.reactions) || 0,
                    reactions_json: typeof shout.reactions_json === 'string'
                        ? JSON.parse(shout.reactions_json)
                        : (shout.reactions_json || {}),
                    poll_options: parsedPollOptions,
                    total_votes: parsedTotalVotes,
                };
            });
        } catch (error) {
            console.error("Error fetching shouts:", error);
            return [];
        }
    }

    const handleExpire = useCallback((slotIdToExpire: string, expiredShoutId: string) => {
        setShoutSlots(prev => prev.map(item => item.slotId === slotIdToExpire ? { ...item, status: 'exiting' } : item));
        setTimeout(() => { attemptReplacement(expiredShoutId, slotIdToExpire, 2); }, EXIT_ANIMATION_DURATION_MS + 50);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const attemptReplacement = useCallback((_expiredShoutId: string, targetSlotId: string, retries: number) => {
        const currentVisibleIds = slotsRef.current
            .filter(slot => slot.data && (slot.status === 'visible' || slot.status === 'entering'))
            .map(slot => slot.data!.id);
        const BATCH_SIZE = 5; // Fetch a small batch to find a replacement

        fetchShouts(BATCH_SIZE, currentVisibleIds)
            .then(potentialReplacements => {
                if (potentialReplacements?.length > 0) {
                    // Try to find one not already visible or entering
                    const replacementData = potentialReplacements.find(potential =>
                        !slotsRef.current.some(slot => slot.data?.id === potential.id && slot.status !== 'exiting')
                    );
                    if (replacementData) {
                        setShoutSlots(prev => prev.map(item => item.slotId === targetSlotId ? { ...item, data: replacementData, status: 'entering' } : item));
                        setTimeout(() => {
                            setShoutSlots(prev => prev.map(slot => slot.slotId === targetSlotId && slot.status === 'entering' ? { ...slot, status: 'visible' } : slot));
                        }, ENTRY_ANIMATION_DURATION_MS);
                    } else {
                        // No suitable replacement found in this batch
                        if (retries > 0) setTimeout(() => attemptReplacement(_expiredShoutId, targetSlotId, retries - 1), 500);
                        else setShoutSlots(prev => prev.map(item => item.slotId === targetSlotId ? { ...item, data: null, status: 'empty' } : item));
                    }
                } else {
                    // No shouts returned at all
                    if (retries > 0) setTimeout(() => attemptReplacement(_expiredShoutId, targetSlotId, retries - 1), 1000);
                    else setShoutSlots(prev => prev.map(item => item.slotId === targetSlotId ? { ...item, data: null, status: 'empty' } : item));
                }
            })
            .catch(error => {
                console.error(`Error fetching replacement for slot ${targetSlotId}:`, error);
                if (retries > 0) setTimeout(() => attemptReplacement(_expiredShoutId, targetSlotId, retries - 1), 1500);
                else setShoutSlots(prev => prev.map(item => item.slotId === targetSlotId ? { ...item, data: null, status: 'empty' } : item));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleEchoShoutAction = (shoutIdToEcho: string) => {
        if (!shoutIdToEcho || echoedShoutIds.has(shoutIdToEcho)) return;
        setEchoedShoutIds(prev => new Set(prev).add(shoutIdToEcho));

        apiClient.patch(`/shouts/${shoutIdToEcho}/echo`)
            .then(response => {
                const rawUpdatedShout: IShout = response.data;
                // Reprocess poll data similar to fetchShouts
                let parsedPollOptions: IPollOption[] | null = null;
                let parsedTotalVotes: number | null = null;
                if (rawUpdatedShout.content_type === 'poll' && rawUpdatedShout.poll_results) {
                    let pollResultsData = rawUpdatedShout.poll_results;
                     if (typeof pollResultsData === 'string') {
                        try { pollResultsData = JSON.parse(pollResultsData); } catch (e) { pollResultsData = { options: [] }; }
                    }
                    parsedPollOptions = (pollResultsData?.options || []).map((opt: any) => ({ id: opt.id, text: opt.text, emoji: opt.emoji, votes: Number(opt.votes) || 0 }));
                    parsedTotalVotes = Number(rawUpdatedShout.poll_responses) || 0;
                }

                const updatedShoutData: IShout = {
                    ...rawUpdatedShout,
                    secondsRemaining: rawUpdatedShout.duration, // Reset if needed, or keep current
                    reactions: Number(rawUpdatedShout.reactions) || 0,
                    reactions_json: typeof rawUpdatedShout.reactions_json === 'string'
                        ? JSON.parse(rawUpdatedShout.reactions_json)
                        : (rawUpdatedShout.reactions_json || {}),
                    poll_options: parsedPollOptions,
                    total_votes: parsedTotalVotes,
                };
                setShoutSlots(prev => prev.map(slot => slot.data?.id === shoutIdToEcho ? { ...slot, data: updatedShoutData } : slot));
                if (modalShout?.id === shoutIdToEcho) {
                     setModalShout(prevModalShout => prevModalShout ? {...updatedShoutData, user_voted_option_id: prevModalShout.user_voted_option_id} : null);
                }
            })
            .catch(err => {
                console.error("Error echoing shout:", err);
                setEchoedShoutIds(prev => { const n = new Set(prev); n.delete(shoutIdToEcho); return n; });
                alert(`Failed to echo shout. ${err.response?.data?.message || 'Please try again.'}`);
            });
    };

    const handleFlagButtonClickInModal = (event: React.MouseEvent<HTMLButtonElement>, shoutIdForFlag: string) => {
        if (flaggedShoutIds.has(shoutIdForFlag)) return;
        event.preventDefault(); // Prevent modal from closing if flag button is inside clickable area
        if (flagPopoverOpen) {
            setFlagPopoverOpen(false);
        } else {
            // Position popover relative to the button if possible, or center screen
            setFlagPopoverOpen(true);
        }
    };

    const handleFlagReasonClick = (flagType: string) => {
        if (!modalShout) return;
        const shoutIdToFlag = modalShout.id;
        if (flaggedShoutIds.has(shoutIdToFlag)) return;

        setFlagPopoverOpen(false); // Close popover

        apiClient.patch(`/shouts/${shoutIdToFlag}/flag`, { flagType })
            .then((response) => {
                setFlaggedShoutIds(prev => new Set(prev).add(shoutIdToFlag));
                // Optionally, update UI to show it's flagged or remove it
                const slotIdToReplace = slotsRef.current.find(slot => slot.data?.id === shoutIdToFlag)?.slotId;
                setModalShout(null); // Close modal after flagging
                if (slotIdToReplace) {
                    handleExpire(slotIdToReplace, shoutIdToFlag); // Trigger replacement
                } else {
                    // If not found in slots (e.g., opened from a non-grid source), just filter it out if it was part of a list
                    setShoutSlots(prev => prev.filter(slot => slot.data?.id !== shoutIdToFlag));
                }
            })
            .catch(err => {
                console.error(`Error flagging shout ${shoutIdToFlag}:`, err);
                alert(`Failed to flag shout. ${err.response?.data?.message || 'Please try again.'}`);
            });
    };

    const handleSubmitShoutFromModal = (payload: CreateModalPayload) => {
        apiClient.post('/shouts', payload)
            .then((response: AxiosResponse<IShout | AutomodRejectionResponse>) => {
                if (response.data && typeof response.data === 'object' && 'status' in response.data && response.data.status === 'rejected') {
                    const rejectionData = response.data as AutomodRejectionResponse;
                    setAutomodFailureReason(rejectionData.reason || null);
                    setAutomodErrorModalOpen(true);
                } else if (response.status === 201) {
                    // Reset the form data for the next shout
                    setInitialModalShoutData({
                        headline: '',
                        contentType: getDefaultContentType(),
                        textContent: '',
                        videoLink: '',
                        imageUrl: DEFAULT_PLACEHOLDER_IMAGE,
                        formatting: { bold: false, italic: false, underline: false, alignment: 'left', textColor: 'white' },
                        pollOptionsData: getDefaultContentType() === 'poll' ? getInitialPollOptions() : [],
                    });
                    setComposing(false); // Close the create shout modal
                    // Optionally, find an empty slot and place the new shout, or refresh a slot
                } else {
                    console.warn("Received unexpected success response structure:", response);
                    alert('Submitted, but received an unexpected response from the server.');
                }
            })
            .catch(err => {
                console.error("Error submitting shout (in .catch):", err);
                // Check if the error response contains automod rejection
                if (err.response && err.response.data && err.response.data.status === 'rejected') {
                    const rejectionData = err.response.data as AutomodRejectionResponse;
                    setAutomodFailureReason(rejectionData.reason || null);
                    setAutomodErrorModalOpen(true);
                } else {
                    alert(`Failed to submit shout. ${err.response?.data?.message || 'An unexpected error occurred. Please try again.'}`);
                }
            });
    };


    const handleReactToShout = (shoutId: string, emoji: string, add: boolean) => {
        // Optimistically update modal's reaction set
        if (modalShout?.id === shoutId) {
            setCurrentUserReactionsForModal(prevReactions => {
                const newReactions = new Set(prevReactions);
                if (add) newReactions.add(emoji);
                else newReactions.delete(emoji);
                return newReactions;
            });
        }

        apiClient.patch(`/shouts/${shoutId}/react`, { emoji, add })
            .then(response => {
                const rawUpdatedShout: IShout = response.data;
                 // Reprocess poll data similar to fetchShouts
                let parsedPollOptions: IPollOption[] | null = null;
                let parsedTotalVotes: number | null = null;
                if (rawUpdatedShout.content_type === 'poll' && rawUpdatedShout.poll_results) {
                    let pollResultsData = rawUpdatedShout.poll_results;
                     if (typeof pollResultsData === 'string') {
                        try { pollResultsData = JSON.parse(pollResultsData); } catch (e) { pollResultsData = { options: [] }; }
                    }
                    parsedPollOptions = (pollResultsData?.options || []).map((opt: any) => ({ id: opt.id, text: opt.text, emoji: opt.emoji, votes: Number(opt.votes) || 0 }));
                    parsedTotalVotes = Number(rawUpdatedShout.poll_responses) || 0;
                }

                const updatedShout: IShout = {
                    ...rawUpdatedShout,
                    secondsRemaining: rawUpdatedShout.duration, // Or keep existing if preferred
                    reactions: Number(rawUpdatedShout.reactions) || 0,
                    reactions_json: typeof rawUpdatedShout.reactions_json === 'string'
                        ? JSON.parse(rawUpdatedShout.reactions_json)
                        : (rawUpdatedShout.reactions_json || {}),
                    poll_options: parsedPollOptions,
                    total_votes: parsedTotalVotes,
                };
                setShoutSlots(prevSlots => prevSlots.map(slot => slot.data?.id === shoutId ? { ...slot, data: updatedShout } : slot));
                if (modalShout?.id === shoutId) setModalShout(prevModalShout => prevModalShout ? {...updatedShout, user_voted_option_id: prevModalShout.user_voted_option_id} : null);
            })
            .catch(err => {
                console.error(`Error reacting to shout ${shoutId}:`, err);
                alert(`Failed to update reaction. ${err.response?.data?.message || 'Please try again.'}`);
                // Revert optimistic update if API call fails
                if (modalShout?.id === shoutId) {
                    setCurrentUserReactionsForModal(prevReactions => {
                        const newReactions = new Set(prevReactions);
                        if (add) newReactions.delete(emoji); // Revert add
                        else newReactions.add(emoji);      // Revert delete
                        return newReactions;
                    });
                }
            });
    };

    const handleVoteOnPollApi = async (shoutId: string, optionId: string): Promise<IShout | void> => {
        try {
            const response = await apiClient.patch(`/shouts/${shoutId}/vote`, { optionId });
            const rawUpdatedShout: IShout = response.data;

            // Reprocess poll data
            let parsedPollOptions: IPollOption[] | null = null;
            let parsedTotalVotes: number | null = null;
            if (rawUpdatedShout.content_type === 'poll' && rawUpdatedShout.poll_results) {
                let pollResultsData = rawUpdatedShout.poll_results;
                if (typeof pollResultsData === 'string') {
                    try { pollResultsData = JSON.parse(pollResultsData); } catch (e) { pollResultsData = { options: [] }; }
                }
                parsedPollOptions = (pollResultsData?.options || []).map((opt: any) => ({ id: opt.id, text: opt.text, emoji: opt.emoji, votes: Number(opt.votes) || 0 }));
                parsedTotalVotes = Number(rawUpdatedShout.poll_responses) || 0;
            }
            
            const updatedShout: IShout = {
                ...rawUpdatedShout,
                secondsRemaining: rawUpdatedShout.duration,
                reactions: Number(rawUpdatedShout.reactions) || 0,
                reactions_json: typeof rawUpdatedShout.reactions_json === 'string'
                    ? JSON.parse(rawUpdatedShout.reactions_json)
                    : (rawUpdatedShout.reactions_json || {}),
                poll_options: parsedPollOptions,
                total_votes: parsedTotalVotes,
                user_voted_option_id: optionId // Important: mark that user has voted for this option
            };

            setShoutSlots(prevSlots => prevSlots.map(slot => slot.data?.id === shoutId ? { ...slot, data: updatedShout } : slot));
            if (modalShout?.id === shoutId) {
                setModalShout(updatedShout);
            }
            setUserVotedPollOptions(prev => ({ ...prev, [shoutId]: optionId }));
            return updatedShout;
        } catch (err: any) {
            console.error(`Error voting on poll ${shoutId} option ${optionId}:`, err);
            alert(`Failed to vote. ${err.response?.data?.message || 'Please try again.'}`);
            throw err; 
        }
    };


    const handleOpenModal = (shoutData: IShout) => {
        setCurrentUserReactionsForModal(new Set()); // Reset for the new modal
        const userVote = userVotedPollOptions[shoutData.id];
        setModalShout(userVote ? { ...shoutData, user_voted_option_id: userVote } : shoutData);
        
        if (shoutData && shoutData.id) {
            apiClient.patch(`/shouts/${shoutData.id}/open`)
                .then(response => {
                    // Optional: log success or handle response if needed
                    // console.log('Shout open event sent for:', shoutData.id);
                })
                .catch(err => {
                    console.error(`Error sending shout open event for ${shoutData.id}:`, err);
                });
        }
    };

    const handleCloseModal = () => {
        setModalShout(null);
        setCurrentUserReactionsForModal(new Set());
        if (flagPopoverOpen) { // Close flag popover if it was open for this modal
            setFlagPopoverOpen(false);
        }
    };


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (colorPickerOpen && colorPickerButtonRef.current && !colorPickerButtonRef.current.contains(event.target as Node) && colorPickerPopoverRef.current && !colorPickerPopoverRef.current.contains(event.target as Node)) { setColorPickerOpen(false); }
        };
        if (colorPickerOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [colorPickerOpen]);

    useEffect(() => {
        const handleClickOutsideFlag = (event: MouseEvent) => {
            // Check if click is outside the flag button (that opened it) AND outside the popover itself
            const isOutsideButton = flagButtonRefForModal.current && !flagButtonRefForModal.current.contains(event.target as Node);
            const isOutsidePopover = flagPopoverRef.current && !flagPopoverRef.current.contains(event.target as Node);

            if (flagPopoverOpen && isOutsideButton && isOutsidePopover) {
                setFlagPopoverOpen(false);
            }
        };
        if (flagPopoverOpen) document.addEventListener('mousedown', handleClickOutsideFlag);
        return () => document.removeEventListener('mousedown', handleClickOutsideFlag);
    }, [flagPopoverOpen]);


    useEffect(() => {
        fetchShouts(SHOUT_COUNT)
            .then(initialShouts => {
                // Ensure no duplicate IDs if API could return them (though /random should be fine)
                const uniqueShouts = initialShouts.filter((s, i, self) => i === self.findIndex(t => t.id === s.id));
                const limitedShouts = uniqueShouts.slice(0, SHOUT_COUNT); // Ensure we don't exceed SHOUT_COUNT
                const initialSlots: IShoutSlot[] = limitedShouts.map((shout, index) => ({ slotId: `slot-${index}-${Date.now()}`, data: shout, status: 'entering' }));
                // Fill remaining slots if fewer shouts than SHOUT_COUNT were fetched
                while (initialSlots.length < SHOUT_COUNT) { initialSlots.push({ slotId: `initial-empty-${initialSlots.length}-${Date.now()}`, data: null, status: 'empty' }); }
                setShoutSlots(initialSlots);
                setTimeout(() => { setShoutSlots(prev => prev.map(slot => (slot.status === 'entering' ? { ...slot, status: 'visible' } : slot))); }, ENTRY_ANIMATION_DURATION_MS);
            })
            .catch(err => {
                console.error("Failed initial shout fetch:", err);
                setShoutSlots(Array(SHOUT_COUNT).fill(null).map((_, i) => ({ slotId: `initial-error-${i}-${Date.now()}`, data: null, status: 'empty' })));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array ensures this runs only once on mount

    const youtubeUtilsProp = { extractYouTubeVideoId, convertToEmbedUrl };

    return (
        <div className="flex flex-col min-h-screen bg-gray-900 text-white font-sans">
            <header className="bg-gray-800 p-4 shadow-lg sticky top-0 z-20">
                <div className="container mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <EchoomiLogo className={`h-8 w-8 ${getThemeColors().secondary}`} />
                        <h1 className={`text-2xl font-bold ${getThemeColors().secondary}`}>Echoomi</h1>
                    </div>
                    <div className="flex gap-2 items-center">
                        <div className="relative color-picker-container">
                            <button ref={colorPickerButtonRef} onClick={() => setColorPickerOpen(!colorPickerOpen)} className={`${getThemeColors().primary} ${getThemeColors().hover} p-2 rounded-full flex items-center justify-center transition-colors duration-150`} aria-label="Change theme color"> <Palette size={20} /> </button>
                            {colorPickerOpen && createPortal(
                                <div ref={colorPickerPopoverRef} className="picker-popover color-picker" style={{ position: 'absolute', top: colorPickerButtonRef.current ? colorPickerButtonRef.current.getBoundingClientRect().bottom + window.scrollY + 8 : 0, left: colorPickerButtonRef.current ? colorPickerButtonRef.current.getBoundingClientRect().right + window.scrollX - 180 : 0 }} onMouseDown={e => e.preventDefault()}>
                                    {colorOptions.map(color => (
                                        <div key={color.name} className={`color-option ${color.primary} ${themeColor === color.name ? 'active' : ''}`} style={{ backgroundColor: color.color }} onClick={() => { setThemeColor(color.name); setColorPickerOpen(false); }} title={color.name} />
                                    ))}
                                </div>,
                                document.body
                            )}
                        </div>
                        <button onClick={() => {
                            setInitialModalShoutData({ // Reset data for new shout
                                headline: '',
                                contentType: getDefaultContentType(),
                                textContent: '',
                                videoLink: '',
                                imageUrl: DEFAULT_PLACEHOLDER_IMAGE,
                                formatting: { bold: false, italic: false, underline: false, alignment: 'left', textColor: 'white' },
                                pollOptionsData: getDefaultContentType() === 'poll' ? getInitialPollOptions() : [],
                            });
                            setComposing(true);
                        }} className={`${getThemeColors().primary} ${getThemeColors().hover} px-4 py-2 rounded-full font-medium flex items-center transition-colors duration-150`} > <Send size={16} className="mr-2" /> New Shout </button>
                    </div>
                </div>
            </header>

            <main className="container mx-auto flex-grow p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {shoutSlots.map(slot => (<ShoutCard key={slot.slotId} slot={slot} onExpire={handleExpire} onClick={handleOpenModal} getThemeColors={getThemeColors} />))}
                </div>
            </main>

            {modalShout && (
                <ShoutModal
                    shout={modalShout}
                    onClose={handleCloseModal}
                    onEcho={handleEchoShoutAction}
                    onFlag={handleFlagButtonClickInModal}
                    onReact={handleReactToShout}
                    onVoteOnPoll={handleVoteOnPollApi}
                    getThemeColors={getThemeColors}
                    echoedShoutIds={echoedShoutIds}
                    flaggedShoutIds={flaggedShoutIds}
                    flagButtonRef={flagButtonRefForModal}
                    initialUserReactions={currentUserReactionsForModal}
                    userVotedOptionId={modalShout.user_voted_option_id || userVotedPollOptions[modalShout.id]}
                />
            )}

            {flagPopoverOpen && modalShout && createPortal( // Ensure modalShout exists for context
                <div ref={flagPopoverRef} className="picker-popover flag-picker" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1050 }} onMouseDown={e => e.preventDefault()}>
                    {FLAG_REASONS.map(reason => (<button key={reason.key} onClick={() => handleFlagReasonClick(reason.key)}>{reason.label}</button>))}
                    <p>Flagged posts are reviewed for moderation. Abuse of the flagged post function is against our terms of use.</p>
                </div>, document.body
            )}

            <CreateShoutModal
                isOpen={composing}
                onClose={() => setComposing(false)}
                onSubmit={handleSubmitShoutFromModal}
                getThemeColors={getThemeColors}
                IS_ANDROID={IS_ANDROID}
                youtubeUtils={youtubeUtilsProp}
                initialShoutState={initialModalShoutData}
            />

            {automodErrorModalOpen && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]" onClick={(e) => { if (e.target === e.currentTarget) setAutomodErrorModalOpen(false); }}>
                    <div className="bg-gray-800 rounded-lg max-w-md w-full shadow-xl p-6 border border-yellow-500/50">
                        <div className="flex items-center justify-center mb-4">
                            {automodFailureReason === 'low_quality' ? (<MessageSquareWarning className="text-yellow-500 mr-3" size={32} />)
                                : automodFailureReason === 'banned_phrase' ? (<ShieldBan className="text-red-500 mr-3" size={32} />)
                                : automodFailureReason === 'link_detected' ? (<Ban className="text-orange-500 mr-3" size={32} />)
                                : (<AlertTriangle className="text-red-500 mr-3" size={32} />)}
                            <h2 className="text-xl font-bold text-yellow-400">
                                {automodFailureReason === 'low_quality' ? "AUTOMOD EXPECTS MORE FROM YOU" :
                                 automodFailureReason === 'banned_phrase' ? 'Community Guidelines Violation' :
                                 automodFailureReason === 'link_detected' ? 'Links Are Not Allowed' :
                                 'Content Violation'}
                            </h2>
                        </div>
                        <p className="text-gray-300 text-center mb-6 whitespace-pre-wrap">
                            {automodFailureReason === 'low_quality' ?
                                "AUTOMOD isn't mad, just disappointed.\n\n You are smart and creative so AUTOMOD would like to see you improve your post. AUTOMOD appologizes if AUTOMOD is wrong as AUTOMOD gets paranoid sometimes about users not reaching their potential."
                                : automodFailureReason === 'link_detected' ?
                                "Woah there buckaroo, that violates our terms and conditions. Links aren't allowed. Please remove any links."
                                :
                                "Woah there buckaroo, that violates our terms and conditions. Please review our community guidelines. Your message cannot be shared."
                            }
                        </p>
                        <div className="flex justify-center">
                            <button
                                onClick={() => { setAutomodErrorModalOpen(false); setAutomodFailureReason(null); }}
                                className={`${automodFailureReason === 'low_quality' ? 'bg-yellow-600 hover:bg-yellow-700'
                                    : automodFailureReason === 'link_detected' ? 'bg-orange-600 hover:bg-orange-700'
                                    : 'bg-red-600 hover:bg-red-700'} text-white px-6 py-2 rounded-full font-medium transition-colors duration-150`}
                            >
                                Got It
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <footer className="bg-gray-800 p-4 shadow-inner mt-auto">
                <div className="container mx-auto text-center text-gray-400 text-sm">
                    <p>© {new Date().getFullYear()} Echoomi - Anonymous shouts into the void.</p>
                    <p className="mt-1">Shouts fade away. Echo to extend their life and reach. Click to view.</p>
                </div>
            </footer>
        </div>
    );
}
